import type { AdminGymDetail, GymTrainer } from '@steado/admin-contract/gyms';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { OwnerBadge } from '@/components/panel/OwnerBadge';
import { FieldRow, FieldRows, Panel } from '@/components/panel/panels';
import { DataTable, type TableColumn, type TableRow } from '@/components/table/DataTable';
import { BadgeCell, TextCell } from '@/components/table/cells';
import type { ToneName } from '@/components/table/tone';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/auth-context';
import { formatDate, formatDateTime, formatRelative } from '@/lib/format';
import { inviteExpired, ownerStatusLine } from '@/lib/gym-owner-status';

import { gymActivityLabel } from './gym-activity-labels';
import { gymDetailQueryOptions, resendOwnerInvite } from './gym-detail-query';

interface GymProps {
  readonly gym: AdminGymDetail;
}

const n = (value: number) => String(value);

/** Who the gym serves, in numbers; the people themselves are one click away on Users. */
export function PeoplePanel({ gym }: GymProps) {
  const { counts } = gym;
  return (
    <Panel
      title="People"
      description="Counted as the owner app counts them. A member whose account was deleted drops out."
      bleed
      footer={
        <Link
          to="/users"
          search={{ gym: gym.id }}
          className="text-[13px] font-medium text-foreground hover:text-primary"
        >
          Open the {n(counts.coached)} coached {counts.coached === 1 ? 'member' : 'members'} in Users
        </Link>
      }
    >
      <FieldRows>
        <FieldRow label="Coached now" value={n(counts.coached)} mono />
        <FieldRow label="Ever coached" value={`${n(counts.peopleEver)} (${n(counts.left)} left)`} mono />
        <FieldRow
          label="Not sharing with their trainer"
          value={n(counts.notSharing)}
          mono
          {...(counts.notSharing > 0 ? ({ tone: 'warn' } as const) : {})}
        />
        <FieldRow label="Waiting for a first plan" value={n(counts.waitingFirstPlan)} mono />
        <FieldRow
          label="Need a trainer"
          value={n(counts.needsTrainer)}
          mono
          {...(counts.needsTrainer > 0 ? ({ tone: 'warn' } as const) : {})}
        />
        <FieldRow label="Join requests waiting" value={n(counts.joinRequestsWaiting)} mono />
        <FieldRow label="Open change requests" value={n(counts.changeRequestsOpen)} mono />
      </FieldRows>
      <p className="border-t border-border px-[18px] py-2.5 text-xs text-muted-foreground">
        Users leaves deactivated accounts out unless you ask for them, so its count can be lower.
      </p>
    </Panel>
  );
}

const STAFF_TONES: Readonly<Record<GymTrainer['status'], ToneName>> = {
  active: 'ok',
  invited: 'info',
  removed: 'mute',
  revoked: 'mute',
  declined: 'mute',
};

const TRAINER_COLUMNS: readonly TableColumn[] = [
  { key: 'name', label: 'Trainer', width: '1.5fr' },
  { key: 'status', label: 'Status', width: '0.9fr' },
  { key: 'members', label: 'Members', width: '0.7fr', align: 'right' },
  { key: 'plans', label: 'Plans sent', width: '0.7fr', align: 'right' },
  { key: 'since', label: 'Since', width: '0.9fr' },
];

function trainerSince(trainer: GymTrainer): { value: string | null; sub: string | null } {
  if (trainer.status === 'invited') {
    const expired = trainer.inviteExpiresAt !== null && new Date(trainer.inviteExpiresAt) < new Date();
    return {
      value: trainer.invitedAt === null ? null : `invited ${formatDate(trainer.invitedAt)}`,
      sub: expired ? 'invite expired' : null,
    };
  }
  if (trainer.removedAt !== null) return { value: `removed ${formatDate(trainer.removedAt)}`, sub: null };
  return { value: trainer.acceptedAt === null ? null : formatDate(trainer.acceptedAt), sub: null };
}

export function TrainersPanel({ gym }: GymProps) {
  const rows: readonly TableRow[] = gym.trainers.map((trainer) => {
    const since = trainerSince(trainer);
    return {
      id: trainer.staffId,
      cells: [
        <span key="name" className="flex min-w-0 flex-col">
          {trainer.userId === null ? (
            <span className="truncate text-sm font-medium">{trainer.displayName}</span>
          ) : (
            <Link
              to="/users/$userId"
              params={{ userId: trainer.userId }}
              search={{ tab: 'profile' }}
              className="truncate text-sm font-medium text-foreground hover:text-primary"
            >
              {trainer.displayName}
            </Link>
          )}
          <span className="truncate font-mono text-xs text-muted-foreground">{trainer.phone}</span>
        </span>,
        <BadgeCell key="status" label={trainer.status} tone={STAFF_TONES[trainer.status]} dot />,
        <TextCell key="members" value={n(trainer.activeMembers)} mono align="right" />,
        <TextCell key="plans" value={n(trainer.plansPublished)} mono align="right" />,
        <TextCell key="since" value={since.value} sub={since.sub} />,
      ],
    };
  });

  return (
    <DataTable
      caption="Trainers"
      title="Trainers"
      count={`${n(gym.counts.trainersActive)} active`}
      columns={TRAINER_COLUMNS}
      rows={rows}
      isLoading={false}
      emptyTitle="No trainers yet"
      emptyBody="The owner has not invited anyone. Members wait for a trainer until they do."
    />
  );
}

/**
 * The owner's account. Steado creates it and emails an invite; until the owner
 * accepts, an admin can send the invite again (the older link stops working).
 */
export function OwnerPanel({ gym }: GymProps) {
  const { owner } = gym;
  const { api, can } = useAuth();
  const queryClient = useQueryClient();
  const resend = useMutation({
    mutationFn: () => resendOwnerInvite(api, gym.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: gymDetailQueryOptions(api, gym.id).queryKey });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'gyms'] });
    },
    retry: false,
  });
  const invited = owner.status === 'invited';

  return (
    <Panel
      title="Owner"
      owner={<OwnerBadge>{invited ? 'Invited by Steado' : 'From the owner app, read only'}</OwnerBadge>}
      bleed
      {...(can.mutate && invited
        ? {
            action: (
              <Button
                type="button"
                variant="outline"
                size="xs"
                disabled={resend.isPending}
                onClick={() => {
                  resend.mutate();
                }}
              >
                {resend.isPending ? 'Sending…' : 'Resend invite'}
              </Button>
            ),
          }
        : {})}
      {...(resend.isSuccess || resend.isError
        ? {
            footer: (
              <span
                role="status"
                className={resend.isError ? 'text-xs text-err-fg' : 'text-xs text-muted-foreground'}
              >
                {resend.isError
                  ? resend.error.message
                  : `Invite sent again to ${owner.email}. The link works until ${formatDate(resend.data.inviteExpiresAt)}; the last one no longer does.`}
              </span>
            ),
          }
        : {})}
    >
      <FieldRows>
        <FieldRow
          label="Status"
          value={ownerStatusLine(owner)}
          {...(inviteExpired(owner) ? ({ tone: 'warn' } as const) : {})}
        />
        {invited ? (
          <FieldRow
            label="Invite sent"
            value={owner.inviteSentAt === null ? null : formatDateTime(owner.inviteSentAt)}
          />
        ) : null}
        <FieldRow label="Name" value={owner.displayName} />
        <FieldRow label="Email" value={owner.email} mono />
        <FieldRow label="Phone" value={owner.phone} mono />
        <FieldRow
          label="Email confirmed"
          value={owner.emailConfirmedAt === null ? 'Not yet' : formatDate(owner.emailConfirmedAt)}
          {...(owner.emailConfirmedAt === null && !invited ? ({ tone: 'warn' } as const) : {})}
        />
        <FieldRow label="Account created" value={formatDate(owner.signedUpAt)} />
        <FieldRow label="Terms accepted" value={owner.termsVersion} />
        <FieldRow
          label="Last seen in the owner app"
          value={owner.lastSeenAt === null ? 'Not yet' : formatRelative(owner.lastSeenAt)}
        />
      </FieldRows>
    </Panel>
  );
}

export function CoachingPanel({ gym }: GymProps) {
  const { counts } = gym;
  return (
    <Panel title="Coaching" description="The last 7 days, then the total." bleed>
      <FieldRows>
        <FieldRow
          label="Plans sent"
          value={`${n(counts.plansPublishedInWindow)} · ${n(counts.plansPublished)} in all`}
          mono
        />
        <FieldRow label="Of those, edits to a running plan" value={n(counts.revisionsPublished)} mono />
        <FieldRow
          label="AI drafts"
          value={`${n(counts.aiDraftsInWindow)} · ${n(counts.aiDrafts)} in all`}
          mono
        />
        <FieldRow label="Messages to members" value={n(counts.messagesInWindow)} mono />
        <FieldRow
          label="Members who logged"
          value={`${n(counts.loggedInWindow)} of ${n(counts.coached)}`}
          mono
        />
      </FieldRows>
    </Panel>
  );
}

export function ActivityPanel({ gym }: GymProps) {
  return (
    <Panel
      title="Recent activity"
      description="Owner sign-ins are left out; the full trail is on Admin activity."
      bleed
      footer={
        <span className="flex flex-wrap gap-x-4 gap-y-1 text-[13px]">
          <Link
            to="/activity"
            search={{ entity: 'Gym', entityId: gym.id }}
            className="font-medium text-foreground hover:text-primary"
          >
            The gym’s trail
          </Link>
          <Link
            to="/activity"
            search={{ entity: 'GymOwnerAccount', entityId: gym.owner.id }}
            className="font-medium text-foreground hover:text-primary"
          >
            The owner account’s trail
          </Link>
        </span>
      }
    >
      {gym.activity.length === 0 ? (
        <p className="border-t border-border px-[18px] py-3.5 text-[13px] text-muted-foreground">
          Nothing yet.
        </p>
      ) : (
        <FieldRows>
          {gym.activity.map((entry) => (
            <FieldRow
              key={entry.id}
              label={formatDateTime(entry.createdAt)}
              value={gymActivityLabel(entry.action)}
            />
          ))}
        </FieldRows>
      )}
    </Panel>
  );
}
